import React, { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  Calendar,
  User,
  Building,
  Tag,
  Shield,
  Clock,
  BellOff,
  Globe,
  MapPin,
  Save,
} from "lucide-react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import { notifyError } from "Common/notify";
import ContactSection from "./components/ContactSection";
import ContactField from "./components/ContactField";
import ContactInfoData from "./components/data";
import { contactTypeOptions, countryOptions, dndModeOptions, dndOriginOptions, stateOptions, timezoneOptions } from "pages/Location/Contacts/Constants";
import { Contact, Option } from "pages/Location/Contacts/components/DataTypes";

const ContactDetails = ({ contact }: { contact: Contact }) => {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [formData, setFormData] = useState<Contact>(contact);
  const [selectedTags, setSelectedTags] = useState<Option[]>([]);
  const { loading, CustomFieldFolders, CustomFields, tags } = ContactInfoData();

  useEffect(() => {
    setFormData(contact);
    setSelectedTags((contact.tags || []).map((t) => ({ value: t, label: t })));
  }, [contact]);

  // 🟢 Unified Input Change
  const handleChange = (
    field: keyof Contact | string,
    value: any,
    parent?: keyof Contact
  ) => {
    if (parent) {
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...(prev[parent] as any), [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };
  // 🔹 Handle change for custom fields
  const handleCustomFieldChange = (
    fieldId: string,
    fieldKey: string,
    value: any
  ) => {
    setFormData((prev) => {
      const existingFields = prev.customFields || [];
      const existingIndex = existingFields.findIndex(
        (f) => f.id === fieldId || f.fieldKey === fieldKey
      );

      let updatedFields;
      if (existingIndex !== -1) {
        // Update existing field
        updatedFields = [...existingFields];
        updatedFields[existingIndex] = {
          ...updatedFields[existingIndex],
          value,
        };
      } else {
        // Add new field
        updatedFields = [...existingFields, { id: fieldId, fieldKey, value }];
      }

      return {
        ...prev,
        customFields: updatedFields,
      };
    });
  };

  // 🟢 Tags Change
  const handleTagsChange = async (newValue: any) => {
    const tags = (newValue || []).map((t: Option) => t.value);
    setSelectedTags(newValue || []);
    setFormData((prev) => ({ ...prev, tags }));
    const payload = {
      tags: tags,
      _id: formData?._id,
    };
    console.log(payload);
    const { response, error } = await AppService.contact.update({ payload });
  };

  const handleCreateTag = async (inputValue: string) => {
    const newTag = { value: inputValue, label: inputValue };
    const updated = [...selectedTags, newTag];
    const payload = {
      name: newTag?.label,
      locationId: locationId, // from user context
    };

    const { response, error } = await AppService.tag.create({
      payload,
    });
    if (error) {
      return notifyError(
        error?.message || "something went wrong while adding tags"
      );
    }
    handleTagsChange(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email && !formData.phone) {
      alert("Either email or phone is required");
      return;
    }
    const payload = {
      ...formData,
      name: `${formData.firstName} ${formData.lastName}`,
      _id: formData?._id,
    };
    console.log(payload);
    const { response, error } = await AppService.contact.update({
      payload,
      toaster: true,
    });
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* CONTACT Section */}
      <ContactSection title="Contact" icon={<User className="w-4 h-4" />} defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactField
            label="First Name"
            value={formData.firstName}
            onChange={(v) => handleChange("firstName", v)}
          />
          <ContactField
            label="Last Name"
            value={formData.lastName}
            onChange={(v) => handleChange("lastName", v)}
          />
          <ContactField
            label="Email"
            value={formData.email || ""}
            onChange={(v) => handleChange("email", v)}
            icon={<Mail className="w-4 h-4" />}
          />
          <ContactField
            label="Phone"
            value={formData.phone || ""}
            onChange={(v) => handleChange("phone", v)}
            icon={<Phone className="w-4 h-4" />}
          />
          <ContactField
            label="Date of Birth"
            type="date"
            value={formData.birthDate?.split("T")[0] || ""}
            onChange={(v) => handleChange("birthDate", v)}
            icon={<Calendar className="w-4 h-4" />}
          />

          {/* Contact Type */}
          <div>
            <label className="block mb-2 text-sm font-medium text-slate-600">
              Contact Type
            </label>
            <Select
              placeholder="Select Contact Type"
              options={contactTypeOptions}
              value={contactTypeOptions.find(
                (opt) => opt.value === formData.contactType
              )}
              onChange={(opt) => handleChange("contactType", opt?.value)}
            />
          </div>
        </div>
      </ContactSection>

      {/* GENERAL INFO */}
      <ContactSection
        title="General Info"
        icon={<Building className="w-4 h-4" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactField
            label="Business Name"
            value={formData.companyName || ""}
            onChange={(v) => handleChange("companyName", v)}
          />
          <ContactField
            label="Website"
            value={formData.companyWebsite || ""}
            onChange={(v) => handleChange("companyWebsite", v)}
            icon={<Globe className="w-4 h-4" />}
          />
          <ContactField
            label="Street Address"
            value={formData.streetAddress || ""}
            onChange={(v) => handleChange("streetAddress", v)}
            icon={<MapPin className="w-4 h-4" />}
          />

          {/* Country */}
          <div>
            <label className="block mb-2 text-sm font-medium text-slate-600">
              Country
            </label>
            <Select
              placeholder="Select Country"
              options={countryOptions}
              value={countryOptions.find(
                (opt) => opt.value === formData.country
              )}
              onChange={(opt) => handleChange("country", opt?.value)}
            />
          </div>

          {/* State */}
          <div>
            <label className="block mb-2 text-sm font-medium text-slate-600">
              State
            </label>
            <Select
              placeholder="Select State"
              options={stateOptions}
              value={stateOptions.find((opt) => opt.value === formData.state)}
              onChange={(opt) => handleChange("state", opt?.value)}
            />
          </div>

          <ContactField
            label="Postal Code"
            value={formData.zipCode || ""}
            onChange={(v) => handleChange("zipCode", v)}
          />

          {/* Timezone */}
          <div>
            <label className="block mb-2 text-sm font-medium text-slate-600">
              Timezone
            </label>
            <Select
              placeholder="Select Timezone"
              options={timezoneOptions}
              value={timezoneOptions.find(
                (opt) => opt.value === formData.timezone
              )}
              onChange={(opt) => handleChange("timezone", opt?.value)}
            />
          </div>
        </div>
      </ContactSection>
      {CustomFieldFolders.map((folder: any) => (
        <ContactSection
          title={folder.name}
          icon={<Building className="w-4 h-4" />}
        >
          {CustomFields.filter((item) => item?.parentId == folder?._id).map(
            (field) => (
              <ContactField
                key={field._id}
                label={field?.name}
                value={
                  formData.customFields?.find(
                    (item) =>
                      item?.id == field._id || item?.fieldKey == field?.fieldKey
                  )?.value || ""
                }
                onChange={(v) =>
                  handleCustomFieldChange(field._id, field.fieldKey, v)
                }
              />
            )
          )}
        </ContactSection>
      ))}

      {/* TAGS */}
      <ContactSection title="Tags" icon={<Tag className="w-4 h-4" />}>
        <label className="block mb-2 text-sm font-medium text-slate-600">
          Tags
        </label>
        <CreatableSelect
          isMulti
          value={selectedTags}
          onChange={handleTagsChange}
          onCreateOption={handleCreateTag}
          options={tags?.map((tag) => ({
            label: tag?.name,
            value: tag?.name,
          }))}
          placeholder="Type to search or create tags..."
        />
      </ContactSection>

      {/* DND SETTINGS */}
      <ContactSection
        title="DND Settings"
        icon={<BellOff className="w-4 h-4" />}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <span className="flex items-center gap-2 text-slate-700">
              <Shield className="w-4 h-4" /> Enable DND
            </span>
            <input
              type="checkbox"
              checked={formData.isDndEnabled || false}
              onChange={(e) => handleChange("isDndEnabled", e.target.checked)}
            />
          </div>

          {formData.isDndEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 border rounded-lg bg-slate-50">
              {/* DND Mode */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-600">
                  DND Mode
                </label>
                <Select
                  placeholder="Select Mode"
                  options={dndModeOptions}
                  value={dndModeOptions.find(
                    (opt) => opt.value === formData.dndPreferences?.mode
                  )}
                  onChange={(opt) =>
                    handleChange("mode", opt?.value, "dndPreferences")
                  }
                />
              </div>

              {/* DND Origin */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-600">
                  DND Origin
                </label>
                <Select
                  placeholder="Select Origin"
                  options={dndOriginOptions}
                  value={dndOriginOptions.find(
                    (opt) => opt.value === formData.dndPreferences?.origin
                  )}
                  onChange={(opt) =>
                    handleChange("origin", opt?.value, "dndPreferences")
                  }
                />
              </div>
            </div>
          )}
        </div>
      </ContactSection>

      {/* ACTIONS */}
      <div className="flex justify-between items-center p-4 border rounded-lg ">
        {formData.createdAt && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            Created: {new Date(formData.createdAt).toLocaleDateString()}
          </div>
        )}
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-custom-500 text-white rounded-lg hover:bg-custom-600"
        >
          <Save className="w-4 h-4" /> Save Changes
        </button>
      </div>
    </form>
  );
};

export default ContactDetails;
